'use client';

import { AppImageClient } from '@components/appImage';
import { LazyVideo } from '@components/lazyVideo';
import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { RoutingMedia } from '../constants';
import {
  ROUTING_EXAMPLE_MEDIA_SIZE,
  type RoutingExampleMedia,
} from '../markupConfigs';

import { useGetClientHost } from '@/shared/hooks';
import { classNames } from '@/shared/lib/classNames/classNames';
import { getDomainKey } from '@/shared/utils/getDomainInfo';

/** Пример маршрута: картинка через AppImage, gif/video — публичный URL как есть */
const renderMedia = (media: RoutingMedia, className: string) => {
  if (media.type === 'image') {
    return (
      <AppImageClient {...media.data} alt="example" className={className} />
    );
  }

  if (media.type === 'video') {
    return (
      <LazyVideo
        {...ROUTING_EXAMPLE_MEDIA_SIZE}
        src={media.src}
        poster={media.poster}
        className={className}
      />
    );
  }

  return (
    <img
      {...ROUTING_EXAMPLE_MEDIA_SIZE}
      src={media.src}
      alt="example"
      className={className}
    />
  );
};

export const RoutingExamplesRedesign = ({
  images,
  examples,
}: {
  images: RoutingExampleMedia;
  examples: {
    titleId: string;
    title: string;
    typeCode: string;
    icon: ReactNode;
  }[];
}) => {
  const [currentType, setCurrentType] = useState('0');
  const domain = getDomainKey(useGetClientHost());

  return (
    <>
      {/** Мобильная версия */}
      <div className="md:hidden">
        <Swiper
          spaceBetween={12}
          slidesPerView={2}
          className="ml-[-8px]! pr-14! pl-2!"
        >
          {examples.map((el) => (
            <SwiperSlide key={el.typeCode}>
              <div
                onClick={() => setCurrentType(el.typeCode)}
                className={classNames({
                  cls: 'rounded-20 flex h-full cursor-pointer flex-col justify-between gap-2 p-4',
                  mods: {
                    'bg-surface-base-inverse-primary':
                      el.typeCode === currentType,
                    'bg-surface-base-secondary': el.typeCode !== currentType,
                  },
                })}
              >
                <span id={el.titleId}>{el.title}</span>
                <span className="bg-surface-base-blur flex h-8 w-8 items-center justify-center rounded-[8px]">
                  {el.icon}
                </span>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="mt-4 mb-6 w-full">
          {Object.entries(images).map(([type, mediaByDomain]) => (
            <Fragment key={type}>
              {renderMedia(
                mediaByDomain[domain],
                classNames({
                  cls: 'rounded-20 h-full w-full object-cover',
                  mods: { hidden: type !== currentType },
                }),
              )}
            </Fragment>
          ))}
        </div>
      </div>

      {/** Desktop версия */}
      <div className="relative z-20 hidden w-full gap-8 max-lg:flex-col-reverse max-lg:items-center md:flex">
        <div className="grid w-full grid-cols-2 gap-4 lg:w-[32%]">
          {examples.map((el) => (
            <div
              key={el.typeCode}
              onClick={() => setCurrentType(el.typeCode)}
              className={classNames({
                cls: 'max-sm rounded-20 flex min-h-[150px] cursor-pointer flex-col justify-between p-4',
                mods: {
                  'bg-surface-base-inverse-primary':
                    el.typeCode === currentType,
                  'bg-surface-base-secondary': el.typeCode !== currentType,
                },
              })}
            >
              <span id={el.titleId}>{el.title}</span>
              <span className="bg-surface-base-blur flex h-8 w-8 items-center justify-center rounded-[8px]">
                {el.icon}
              </span>
            </div>
          ))}
        </div>
        {/* Картинка — прямой ребёнок колонки-флекс-элемента: только так
            h-full тянет её на всю высоту ряда, как в дизайне */}
        <div className="w-full lg:w-[65%]">
          {Object.entries(images).map(([type, mediaByDomain]) => (
            <Fragment key={type}>
              {renderMedia(
                mediaByDomain[domain],
                classNames({
                  cls: 'rounded-20 h-full w-full object-cover max-lg:mb-6',
                  mods: { hidden: type !== currentType },
                }),
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </>
  );
};
